import type { Dictionary } from "./types";

// Japanese is newly authored (no prior in-repo reference). Machine-assisted;
// flag for native review before public launch — see the residual-risk note in
// the i18n rollout summary.
export const ja = {
  common: {
    signIn: "サインイン",
    signOut: "サインアウト",
    settings: "設定",
    help: "ヘルプ",
    add: "追加",
    account: "アカウント",
    cancel: "キャンセル",
    apply: "適用",
    dismiss: "閉じる",
    retry: "再試行",
    save: "保存",
    loading: "読み込み中",
    openWorkspace: "ワークスペースを開く",
    language: "言語",
    signInWith: (provider) => `${provider} でサインイン`
  },
  dataTable: {
    rows: (count) => `${count} 行`,
    columns: "列"
  },
  entities: {
    person: "人物",
    organisation: "会社",
    event: "イベント",
    opportunity: "商談",
    note: "メモ",
    personSingular: "連絡先",
    organisationSingular: "会社",
    eventSingular: "イベント",
    opportunitySingular: "商談",
    noteSingular: "メモ",
    nodeFallback: "ノード"
  },
  nav: {
    overview: "概要",
    graph: "グラフ",
    people: "人物",
    companies: "会社",
    events: "イベント",
    schedule: "スケジュール",
    signals: "商務",
    current: "現在",
    privateAgent: "エージェント",
    workspace: "ワークスペース",
    connectors: "コネクター"
  },
  login: {
    tagline: "すべての会話が、生きた人脈グラフになる。",
    headingIn: "おかえりなさい",
    headingOut: "Salesko にサインイン",
    subIn: "セッションの準備ができました。",
    subOut: "Salesko アカウントでワークスペースを開きます。",
    subOutUnavailable: "Salesko アカウントのサインインはまだ設定されていません。",
    continueWith: (provider) => `${provider} で続ける`,
    openWithout: "サインインせずに続ける",
    notConfigured: "サインイン方法が設定されていません。",
    fineLead: "続行すると、以下に同意したものとみなされます：",
    terms: "利用規約",
    privacy: "プライバシーポリシー",
    and: "および",
    period: "。"
  },
  workspace: {
    contactsIntelligenceWorkspaceAriaLabel: "Salesko 連絡先インテリジェンスワークスペース",
    headerAriaLabel: "ワークスペースヘッダー",
    searchPlaceholder: "人物・会社・イベントを検索…",
    searchAriaLabel: "グラフを検索",
    searchResultsLabel: "グラフ検索結果",
    tenantControlsAriaLabel: "テナント管理",
    opportunitySignalsAriaLabel: "商談シグナル",
    canonicalRegistryAriaLabel: "正規エンティティレジストリ",
    selectedEntityAriaLabel: "選択中のエンティティ",
    detailPanelToggleAriaLabel: "詳細パネルの表示切り替え",
    deleteConversationConfirm: "この会話を削除しますか？この操作は元に戻せません。",
    currentCount: (count) => `現在 ${count} 件`,
    savedCount: (count) => `保存済み ${count} 件`,
    searchingSaved: "保存済みを検索中",
    noCurrentMatch: "現在のフレームに一致するノードがありません。",
    savedFrames: "保存済みフレーム",
    canonicalMatches: "正規データの一致",
    unavailable: "利用できません",
    searchingSavedFrames: "保存済みフレームを検索中…",
    noSavedMatch: "保存済みフレームに一致するノードがありません。",
    viewsLabel: "ビュー",
    accountMenuLabel: "アカウントメニュー",
    noProviderConfigured: "サインイン方法が設定されていません",
    quotaUnlimited: "無制限",
    quotaUnlimitedTooltip: "ご利用のプランでは Agent メッセージが無制限です",
    quotaRemaining: (remaining, limit) =>
      `本日の Agent メッセージ残り ${remaining} / ${limit} 件 · 00:00 UTC にリセット`
  },
  accountSurface: {
    ariaLabel: "アカウント",
    syncCopy: "フレーム、シグナル、テナントアクセスを同期します。",
    tenantLabel: (tenantId) => `テナント ${tenantId}`,
    noProviderConfigured: "サインイン方法が設定されていません。",
    sessionGrantReady: "セッション権限の準備ができています"
  },
  authProviders: {
    signInOptions: "サインインオプション"
  },
  workspaceError: {
    eyebrow: "ワークスペースエラー",
    title: "Salesko のグラフワークスペースを描画できませんでした。",
    fallback: "ワークスペースで予期しない描画エラーが発生しました。",
    retry: "ワークスペースを再試行"
  },
  graphStage: {
    graphFieldAriaLabel: "グラフフィールド",
    relationshipGraphLabel: "人脈グラフ",
    viewModeLabel: "グラフ表示モード",
    show2dTitle: "Obsidian 風の 2D フォースグラフを表示",
    timelineEnabledTitle: "イベントを時間順に並べる",
    timelineDisabledTitle: "タイムラインを作るには 2 件以上のイベントが必要です",
    createShareAriaLabel: "読み取り専用共有リンクを作成",
    sharedReadOnlyTitle: "共有ビューは読み取り専用です",
    copyShareTitle: "読み取り専用共有リンクをコピー",
    openSettingsAriaLabel: "ワークスペース設定を開く",
    openSettingsTitle: "設定を開く",
    openGuideAriaLabel: "ワークスペースガイドを開く",
    openGuideTitle: "ワークスペースガイドを開く",
    toggleFullscreenAriaLabel: "グラフの全画面表示を切り替え",
    toggleFullscreenTitle: "全画面表示を切り替え",
    loadingGraphField: "グラフフィールドを読み込み中…",
    agentFrameWaiting: "Agent フレームを待機中。",
    graphNodesAriaLabel: "グラフノード",
    nodeEdgeCount: (nodeCount, edgeCount) => `ノード ${nodeCount} 件、関係 ${edgeCount} 件。`,
    projectedGraphCount: (visibleNodeCount, sourceNodeCount, visibleEdgeCount, sourceEdgeCount) =>
      `ノード ${visibleNodeCount}/${sourceNodeCount} 件、関係 ${visibleEdgeCount}/${sourceEdgeCount} 件を表示中。`,
    layoutDegraded: "バックグラウンド最適化を利用できないため、即時レイアウトを表示しています。",
    selectNode: (label) => `${label} を選択`,
    selectRelationship: (label) => `関係 ${label} を選択`,
    flatGraphAriaLabel: "2D フォースグラフ",
    timelineGraphAriaLabel: "タイムライン関係グラフ",
    timelineLabel: "タイムライン",
    timelineMomentsAriaLabel: "タイムラインイベント",
    previousTimelineMoment: "前のイベント",
    nextTimelineMoment: "次のイベント",
    timelinePosition: (position, total) => `${position}/${total} 件目のイベント`,
    dateUnknown: "日付不明",
    noLinkedEntities: "関連エンティティなし",
    linkedEntityCount: (count) => `関連エンティティ ${count} 件`,
    timelineNeedsTwoEvents: "タイムラインには 2 件以上のイベントが必要です。",
    addDatedInteraction: "日付付きのやり取りを追加",
    setOpeningMomentTitle: "このイベントをフレームの開始時点として保存",
    setOpeningMoment: "開始時点に設定",
    graphControlsAriaLabel: "グラフ表示コントロール",
    recenter: "再センタリング",
    zoomIn: "拡大",
    zoomOut: "縮小",
    navigatorAriaLabel: "グラフナビゲーター。ドラッグしてグラフを回転します。",
    warningsCount: (count) => ` 警告 ${count} 件。`
  },
  frameIo: {
    dismissStatus: "フレームのインポート/エクスポート/共有状態を閉じる",
    exporting: (format) => `${format} artifact をエクスポート中。`,
    importing: (fileName, format) => `${fileName} を新しい ${format} フレームとしてインポート中。`,
    importingSimple: (fileName) => `${fileName} をインポート中。`,
    creatingShareLink: "読み取り専用共有リンクを作成中。",
    creatingShareLinkShort: "共有リンクを作成中。",
    downloaded: (format) => `${format} artifact をダウンロードしました`,
    downloadedFile: (filename) => `${filename} をダウンロードしました。`,
    copiedShareLink: "読み取り専用共有リンクをコピーしました",
    shareLinkReadyNoCopy: "読み取り専用共有リンクは準備できましたが、ブラウザーでコピーできません",
    shareLinkReady: "共有リンクの準備ができました。",
    shareExpires: (date) => `${date} に期限切れ。`,
    importedSummary: (fileName, nodeCount, edgeCount) =>
      `${fileName} をインポートしました：ノード ${nodeCount} 件、関係 ${edgeCount} 件。`,
    importedOpening: (fileName) => `${fileName} をインポートしました。フレームを開いています。`,
    fileFallback: "ファイル",
    obsidianFilesLabel: (count) => `Obsidian ファイル ${count} 件`,
    errors: {
      exportFailed: "フレームのエクスポートに失敗しました。",
      shareLinkFailed: "共有リンクを作成できませんでした。",
      importFailed: "フレームのインポートに失敗しました。",
      exportNeedsCommittedFrame: "エクスポートする前に、グラフフレームを生成して確定してください。",
      shareNeedsCommittedFrame: "共有する前に、グラフフレームを生成して確定してください。",
      jsonImportFailed: "JSON インポートに失敗しました。",
      obsidianImportFailed: "Obsidian インポートに失敗しました。"
    },
    fileErrors: {
      jsonTooLarge: "JSON グラフインポートは 5 MB までです。",
      jsonInvalid: (fileName) => `${fileName} は有効な JSON ではありません。`,
      jsonInvalidShape: "JSON インポートには Salesko のグラフエクスポートまたは人脈グラフファイルが必要です。",
      obsidianEmptySelection: "Obsidian Markdown ファイルまたはエクスポート artifact を 1 つ以上選択してください。",
      obsidianJsonTooLarge: "Obsidian JSON artifact インポートは 5 MB までです。",
      obsidianJsonInvalidShape: "Obsidian JSON インポートには Salesko Obsidian グラフエクスポートが必要です。",
      obsidianMarkdownTooLarge: (fileName) => `${fileName} は Obsidian ファイル上限 100 KB を超えています。`
    }
  },
  changeSet: {
    dismissStatus: "変更セット状態を閉じる",
    importingFile: (fileName) => `${fileName} を保留中の変更セットにインポート中。`,
    creatingFor: (sourceLabel) => `${sourceLabel} の保留中の変更セットを作成中。`,
    pendingSummary: (changeSetId, sourceLabel, commandCount, nodeCount, edgeCount) =>
      nodeCount === undefined || edgeCount === undefined
        ? `保留中の変更セット ${changeSetId}（${sourceLabel}）：コマンド ${commandCount} 件。`
        : `保留中の変更セット ${changeSetId}（${sourceLabel}）：コマンド ${commandCount} 件、ノード ${nodeCount} 件、関係 ${edgeCount} 件。`,
    applying: (changeSetId, sourceLabel) => `変更セット ${changeSetId}（${sourceLabel}）を適用中。`,
    reverting: (changeSetId, sourceLabel) => `変更セット ${changeSetId}（${sourceLabel}）を取り消し中。`,
    applied: (changeSetId, projectionStatus) => `変更セット ${changeSetId} を適用しました。projection ${projectionStatus}。`,
    reverted: (changeSetId, projectionStatus) => `変更セット ${changeSetId} を取り消しました。projection ${projectionStatus}。`,
    apply: "適用",
    revert: "取り消し",
    pendingPanel: (changeSetId, sourceLabel) => `保留中の変更セット ${changeSetId} は ${sourceLabel} から作成されました。`,
    updatingSource: (action, sourceLabel) => `${action} ${sourceLabel}。`,
    sourceUpdated: (sourceLabel) => `${sourceLabel} を更新しました。フレームを開いています。`,
    errors: {
      historyFailed: "変更セット履歴の読み込みに失敗しました。",
      auditLogFailed: "変更セット audit log の読み込みに失敗しました。",
      auditExportFailed: "Audit のエクスポートに失敗しました。",
      csvImportFailed: "CSV インポートに失敗しました。",
      createFailed: "変更セットを作成できませんでした。",
      applyFailed: "変更セットを適用できませんでした。",
      revertFailed: "変更セットを取り消せませんでした。",
      csvNeedsCommittedFrame: "CSV をインポートする前に、グラフフレームを生成して確定してください。",
      createNeedsCommittedFrame: "グラフ変更を作成する前に、グラフフレームを生成して確定してください。",
      csvTooLarge: "この API slice の CSV インポートは 100 KB までです。",
      csvEmpty: "CSV ファイルは空です。"
    },
    confirm: {
      csvImport: (fileName) => `${fileName} を現在のグラフフレームの保留中変更セットとしてインポートしますか？`,
      create: (sourceLabel) => `${sourceLabel} の保留中変更セットを作成しますか？`,
      apply: (summary) => `${summary} をこのグラフフレームに適用しますか？`,
      applyImportSummary: (commandCount, sourceLabel) => `${sourceLabel} からのコマンド ${commandCount} 件`,
      applyChangeSetSummary: (changeSetId) => `変更セット ${changeSetId}`,
      revert: (changeSetId, sourceLabel) => `${sourceLabel} の変更セット ${changeSetId} を取り消しますか？`
    },
    sources: {
      nodeEdit: (label) => `ノード編集：${label}`,
      relationship: (sourceLabel, targetLabel) => `関係：${sourceLabel} -> ${targetLabel}`,
      relationshipEdit: (label) => `関係編集：${label}`,
      relationshipDelete: (label) => `関係削除：${label}`,
      relationshipTouchpoint: (label) => `関係 touchpoint：${label}`,
      timelineReplay: (label) => `タイムライン replay：${label}`,
      leadStageMoveIn: (label) => `現在に移動：${label}`,
      leadStageMoveOut: (label) => `現在から削除：${label}`,
      targetFallback: "対象",
      currentMoment: "現在の時点"
    }
  },
  leftRail: {
    entitiesLabel: "エンティティ",
    toggleEntity: (label) => `${label} を切り替え`,
    relationshipStrengthLabel: "フレームの関係性の強さ",
    strengthWeak: "弱い",
    strengthMedium: "普通",
    strengthStrong: "強い",
    recentFrames: "最近のフレーム",
    loadingFrames: "確定済みフレームを読み込み中…",
    frameHistoryUnavailable: "フレーム履歴は現在利用できません。",
    noFramesYet: "確定済みフレームがここに表示されます。",
    frameNodeCount: (count) => `${count} 件のノード`,
    frameLoadError: (frameId) => `フレーム ${frameId} を読み込めませんでした。`,
    recentConversations: "最近の会話",
    newConversationAriaLabel: "新しい会話を始める",
    newConversationTitle: "新しい会話",
    loadingConversations: "会話を読み込み中…",
    conversationHistoryUnavailable: "会話履歴は現在利用できません。",
    noConversationsYet: "会話がここに表示されます。",
    threadMessageCount: (count) => `${count} 件のメッセージ`,
    deleteConversationTitle: "会話を削除",
    deleteConversationAriaLabel: (title) => `${title} の会話を削除`,
    conversationLoadError: (threadId) => `会話 ${threadId} を読み込めませんでした。`
  },
  onboarding: {
    eyebrowGuide: "ワークスペースガイド",
    heading: "グラフフレームから始める",
    graphSummary: (nodeCount, edgeCount) => `ノード ${nodeCount} 件 · 関係 ${edgeCount} 件を表示中`,
    firstGraphSectionLabel: "最初のグラフの流れ",
    eyebrowFirstGraph: "最初のグラフ",
    captureContextTitle: "コンテキストを取り込む",
    captureContextBody: "商談メモ、通話メモ、案件のコンテキストを Agent の入力欄に貼り付けます。",
    reviewGraphTitle: "グラフを確認する",
    reviewGraphBody: "候補を確認し、ノードや関係を編集してから、保留中の変更を適用します。",
    keepFrameTitle: "フレームを保持する",
    keepFrameBody: "サインイン済みのワークスペースでは、最近のフレームから確定済みフレームを復元できます。",
    openAgent: "Agent を開く",
    startInGraph: "グラフから始める",
    workspaceStatusLabel: "ワークスペースの状態",
    signedInStatus: "サインイン済み",
    signInToPersistStatus: "サインインしてフレームを保持",
    frameHistoryActiveStatus: "フレーム履歴が有効",
    noCommittedFrameStatus: "確定済みフレームはまだありません",
    readOnlySharedViewStatus: "読み取り専用の共有ビュー",
    workspaceEditableStatus: "ワークスペースは編集可能",
    signInOptionsLabel: "ワークスペースのサインインオプション"
  },
  agentChat: {
    graphChat: "Graph Chat",
    openAgentInputAriaLabel: "Agent 入力を開く",
    agentInputAriaLabel: "Agent 入力",
    headline: "コンテキストをグラフに変換",
    subtitle: "無料 · 即時 · あなたが渡したコンテキストを使用",
    signedInTitle: "サインイン済み",
    signInOptionsAriaLabel: "サインインオプション",
    collapseAgentInputAriaLabel: "Agent 入力を折りたたむ",
    collapseTitle: "折りたたむ",
    agentConversationAriaLabel: "Agent との会話",
    exampleNoteAriaLabel: "サンプルの説明",
    exampleOnly: "サンプルです",
    useExample: "この例を使う",
    seeExample: "例を見る",
    messageLabel: "メッセージ",
    messagePlaceholder: "メモを貼り付けるか、Graph Chat にグラフの更新を依頼してください。@ を入力するとエンティティを指定できます…",
    mentionListAriaLabel: "グラフのエンティティをメンション",
    mentionWithText: (text) => `「${text}」をメンション`,
    mentionCurrentEntity: "現在のグラフのエンティティをメンション",
    noMentionMatches: "現在のフレームに一致するエンティティがありません。",
    composerHint: "人物・イベント・会社・商談",
    generating: "生成中…",
    send: "送信",
    structuringGraphFrame: "グラフフレームを構造化中",
    requestFailed: "リクエストが失敗しました",
    needsReview: "要確認",
    executed: "実行済み",
    nodesLabel: "ノード",
    edgesLabel: "関係",
    peopleLabel: "人物",
    eventsLabel: "イベント",
    sourceModel: "モデル",
    sourceRules: "ルール",
    sourceAgent: "Agent",
    genericAgentName: "Salesko Agent",
    genericAgentSubtitle: "Salesko 上で実行",
    handoff: (agentName) => `${agentName} に引き継ぐ`,
    streamStructuring: "Graph Chat で関係コンテキストを構造化しています。",
    streamAuthRequired: "このメモを保存する前に、Graph Chat のモデル抽出が必要です。",
    streamRebuilding: "コンテキストを読み取り、連絡先グラフを再構築しています。",
    streamSeekerStarting: "グラフを読み取り専用で確認し、話す準備をしています。",
    previewStreamFailed: "Agent プレビューストリームに失敗しました。",
    noteIntakeExtractionFailure: "Agent LLM 抽出で graph frame が生成されなかったため、メモは保存されませんでした。",
    noteIntakeExtractionFailureReason: (reasons) => `理由: ${reasons}`,
    noteIntakeCapturing: "intake pipeline でメモを取り込んでいます。",
    modeToggleAriaLabel: "会話モード",
    modeWriterLabel: "ライター",
    modeSeekerLabel: "相談",
    modeWriterHint: "ライター: この会話でグラフが更新されます。",
    modeSeekerHint: "相談: 読み取り専用の相談で、グラフは変更されません。",
    seekerMessagePlaceholder: "相談モードに関係グラフについて話したり分析してもらいましょう — 読み取り専用で変更は行われません…"
  },
  detail: {
    emptyTitle: "エンティティが選択されていません",
    selfMarkedTitle: "このノードはあなたとしてマークされています。クリックすると解除します",
    selfMarkTitle: "このノードをあなた（グラフの中心）としてマーク",
    selfYou: "あなた",
    selfSet: "あなたに設定",
    leadStageInTitle: "このノードを現在の対象としてマーク",
    leadStageOutTitle: "このノードは現在に含まれています。クリックすると削除します",
    leadStageInLabel: "現在に移動",
    leadStageOutLabel: "現在に登録済み",
    dismissCalendarEvent: "イベントを非表示",
    dismissCalendarEventPending: "非表示にしています…",
    dismissCalendarEventDone: "グラフからイベントを非表示にしました。",
    dismissCalendarEventError: "このカレンダーイベントを非表示にできませんでした。もう一度お試しください。",
    about: "概要",
    followUp: "フォローアップ",
    contact: "連絡先",
    relationshipContext: "関係コンテキスト",
    personalDetails: "個人情報",
    relationshipStageField: "関係ステージ",
    relationshipSourceField: "関係ソース",
    contactActionsAriaLabel: "連絡アクション",
    emailAction: "メール",
    callAction: "電話",
    staleByDays: (days) => `${days} 日超過`,
    noAboutYet: "まだ概要がありません。",
    source: "情報源",
    editNodeTitle: "選択中のノードを編集",
    commitBeforeEditNodesTitle: "ノードを編集する前にフレームを確定してください",
    editNodeAriaLabel: "選択中のノードを編集",
    labelField: "ラベル",
    summaryField: "要約",
    evidenceField: "根拠",
    roleField: "役割",
    companyField: "会社",
    stageField: "ステージ",
    sourceField: "ソース",
    timeTextField: "時間テキスト",
    genderField: "性別",
    birthdayField: "誕生日",
    interestsField: "興味・関心",
    emailField: "メール",
    phoneField: "電話",
    locationField: "所在地",
    industryField: "業界",
    sizeField: "従業員規模",
    revenueField: "年間売上",
    websiteField: "ウェブサイト",
    fieldOptionUnset: "未設定",
    genderOptions: {
      male: "男性",
      female: "女性",
      other: "その他",
      undisclosed: "回答しない"
    },
    industryOptions: {
      technology: "テクノロジー",
      finance: "金融",
      healthcare: "ヘルスケア",
      manufacturing: "製造",
      retail: "小売",
      education: "教育",
      media: "メディア",
      realEstate: "不動産",
      energy: "エネルギー",
      consulting: "コンサルティング",
      government: "官公庁",
      other: "その他"
    },
    saveAsChangeSet: "変更セットとして保存",
    canonicalIdentityAriaLabel: "正規 ID",
    identity: "ID",
    editIdentityTitle: "正規 ID を編集",
    commitBeforeEditIdentityTitle: "ID を編集する前にフレームを確定してください",
    editIdentityAriaLabel: "正規 ID を編集",
    identityActionRunning: (action, sourceLabel) => `${action} ${sourceLabel}`,
    identityActionSaved: (action) => `${action} を保存しました`,
    splitLabel: "分割ラベル",
    splitSelected: "選択項目を分割",
    rekeyLabel: "キー再設定ラベル",
    rekeyIdentity: "ID のキーを再設定",
    mergeTarget: "マージ先",
    noSameTypeTarget: "同じ種類の候補がありません",
    signalsPrefix: (signals) => `シグナル：${signals}`,
    mergeIdentity: "ID をマージ",
    reviewCandidateAriaLabel: "候補を確認",
    review: "確認",
    reviewCount: (count) => (count === 1 ? "確認が必要な候補 1 件" : `確認が必要な候補 ${count} 件`),
    extractionConfidence: "抽出信頼度",
    reviewReasonAgentFlagged: "Agent が確認対象としてマーク",
    reviewReasonLowConfidence: (percent) => `信頼度 ${percent}%`,
    accept: "承認",
    discard: "破棄",
    linkedEntities: "関連エンティティ",
    viewAllLinkedTitle: (label) => `正規レジストリですべての ${label} を表示`,
    viewAllLinkedAriaLabel: (count, label) => `正規レジストリで ${count} 件の ${label} を表示`,
    viewAllLinked: (count) => `すべて表示 ${count}`,
    createRelationshipTitle: "関係を作成",
    commitBeforeCreateRelationshipTitle: "先に 2 件以上のノードを含むフレームを確定してください",
    createRelationshipAriaLabel: "関係を作成",
    targetField: "ターゲット",
    typeField: "種類",
    relationshipTypeRelatedTo: "関連",
    relationshipTypeMetAt: "会った場所",
    relationshipTypeWorksAt: "勤務先",
    relationshipTypeAttended: "参加",
    relationshipTypeIntroducedBy: "紹介元",
    relationshipTypeOpportunityAt: "商談先",
    relationshipTypeMentioned: "言及",
    strengthField: (percent) => `強さ ${percent}%`,
    saveRelationship: "関係を保存",
    editingRelationship: (endpoint, edgeId) => `${endpoint} · ${edgeId} を編集中`,
    saveRelationshipChanges: "関係の変更を保存",
    existingRelationshipsAriaLabel: "既存の関係",
    editRelationshipAriaLabel: (label) => `${label} の関係を編集`,
    deleteRelationshipAriaLabel: (label) => `${label} の関係を削除`,
    editRelationshipTitle: "関係を編集",
    commitBeforeEditRelationshipsTitle: "関係を編集する前にフレームを確定してください",
    deleteRelationshipTitle: "関係を削除",
    commitBeforeDeleteRelationshipsTitle: "関係を削除する前にフレームを確定してください",
    linkedGroupsAriaLabel: "関連エンティティグループ",
    noLinkedYet: (label) => `関連する ${label} はまだありません。`,
    frameCommit: "フレーム確定",
    sharedStatus: "共有済み",
    previewStatus: "プレビュー",
    idleStatus: "待機中",
    readOnlyGraphFrame: "読み取り専用グラフフレーム",
    defaultGraphFrame: "デフォルトグラフフレーム",
    waitingForGraphFrame: "グラフフレーム待機中",
    advanced: "高度",
    relationshipDirectionOut: "この実体から",
    relationshipDirectionIn: "この実体へ",
    identityActionMerge: "マージ",
    identityActionRekey: "キー再設定",
    identityActionSplit: "分割"
  },
  relationshipIntel: {
    ariaLabel: "関係インテリジェンス",
    title: "関係インテリジェンス",
    signInCommittedFrame: "サインインして確定済みフレームを開くと、関係インテリジェンスを読み込めます。",
    addAnotherEntity: "紹介経路を調べるには、もう 1 件エンティティを追加してください。",
    targetLabel: "紹介経路のターゲット",
    pathsTo: (label) => `${label} への経路`,
    introductionPaths: "紹介経路",
    loadingPaths: "紹介経路を読み込み中",
    pathsUnavailable: "関係経路は現在利用できません。",
    noPath: "この組み合わせの紹介経路は見つかりませんでした。",
    commonNeighbor: "共通の隣接ノード",
    direct: "直接",
    seenInFrames: (count) => `${count} 件のフレームで確認`,
    staleReminders: "古い関係のリマインダー",
    loadingStale: "古い関係を読み込み中",
    remindersUnavailable: "関係リマインダーは現在利用できません。",
    noStaleForEntity: (count) => `このエンティティに古い関係はありません。このテナントの別の場所に ${count} 件あります。`,
    noStaleOlderThan: (days) => `${days} 日より古い関係はありません。`,
    lastSeenStaleBy: (lastSeen, days) => `最終確認 ${lastSeen} · ${days} 日経過`,
    recordTouchpointTitle: "タッチポイントを保留中の変更セットとして記録",
    openLastSeenFrameTitle: "この関係が最後に確認されたフレームを開く",
    logTouchpoint: "タッチポイントを記録",
    openInFrame: "フレームで開く"
  },
  changeSetHistory: {
    ariaLabel: "変更セット履歴",
    title: "変更セット",
    refreshAriaLabel: "変更セット履歴を更新",
    refreshTitle: "変更セット履歴を更新",
    noCommit: "mutation 履歴を見るには、フレームを生成して確定してください。",
    loading: "変更セット履歴を読み込み中",
    empty: "このフレームに変更セットは記録されていません。",
    downloaded: (filename) => `${filename} をダウンロードしました`,
    auditLog: "Audit log",
    auditLogFor: (changeSetId) => `${changeSetId} の audit log`,
    loadingAuditLog: "audit log を読み込み中",
    emptyAuditLog: "この変更セットに audit log イベントはありません。",
    graphMutation: "グラフ mutation",
    commandCount: (count) => `コマンド ${count} 件`,
    exportAuditAriaLabel: (changeSetId) => `${changeSetId} の audit artifact をエクスポート`,
    inspectAuditAriaLabel: (changeSetId) => `${changeSetId} の audit log を確認`,
    exportAuditTitle: "audit JSON をエクスポート",
    auditLogTitle: "Audit log",
    columnChangeSet: "変更セット",
    columnStatus: "状態",
    columnCommands: "コマンド",
    columnUpdated: "更新",
    columnActions: "操作",
    columnEvent: "イベント",
    columnCreated: "作成",
    columnDetails: "詳細",
    auditCreated: "作成済み",
    auditApplied: "適用済み",
    auditReverted: "取り消し済み",
    privateAgentSource: "Private Agent"
  },
  signals: {
    title: "商談受信箱",
    eyebrow: "保存済みグラフ履歴全体",
    inboxSummary: (pending, saved) => `表示中: 保留 ${pending} 件 · 保存済み ${saved} 件`,
    unavailable: "商談受信箱は利用できません",
    viewModeAriaLabel: "商談受信箱の表示モード",
    cardViewTitle: "カード表示",
    tableViewTitle: "テーブル表示",
    cards: "カード",
    table: "テーブル",
    signInToLoad: "非公開の商談受信箱を読み込むにはサインインしてください。",
    loading: "商談受信箱を読み込み中…",
    empty: "保留中または保存済みの商談はありません。",
    actionsFor: (title) => `${title} の操作`,
    openSourceFor: (title) => `${title} のソースフレームを開く`,
    openSourceFrame: "ソースフレームを開く",
    updated: "更新",
    evidence: "根拠",
    source: "ソース",
    confidence: (score) => `信頼度 ${score}%`,
    whySurfaced: "表示理由",
    followUpRequested: (date) => `未スケジュールのフォローアップを追加 ${date}`,
    details: "詳細",
    reasonUnavailable: "この商談の表示理由は記録されていません。",
    evidenceAvailable: "利用可能",
    evidenceUnavailable: "利用不可",
    evidenceSpanCount: (count) => `evidence span ${count} 件`,
    relatedEntities: "関連エンティティ",
    relatedFrame: "関連フレーム",
    sourceAvailable: "ソースフレームあり",
    sourceUnavailable: "利用不可",
    suggestedNextAction: "推奨される次のアクション",
    status: {
      pending: "保留",
      saved: "保存済み",
      dismissed: "非表示",
      archived: "アーカイブ済み"
    },
    actions: {
      dismiss: "非表示",
      save: "商談を保存",
      saved: "保存済み",
      followUp: "未スケジュールのフォローアップを追加",
      followUpAdded: "フォローアップ追加済み"
    },
    columns: {
      actions: "操作",
      evidence: "根拠",
      score: "信頼度",
      source: "ソース",
      status: "状態",
      title: "タイトル",
      updatedAt: "更新"
    }
  },
  activityFeed: {
    surfaceAriaLabel: "商務アクティビティと商談機会",
    closeSurface: "商務パネルを閉じる",
    tabsAriaLabel: "商務機能",
    activityTab: "アクティビティフィード",
    inboxTab: "商談受信箱",
    title: "アクティビティフィード",
    eyebrow: "記録済みの事実と変更",
    pageSummary: (count, hasNext) => hasNext ? `最近の ${count} 件を表示` : `${count} 件`,
    unavailable: "アクティビティフィードは利用できません",
    loading: "アクティビティフィードを読み込み中…",
    empty: "記録済みのアクティビティはまだありません。",
    recordedAt: "記録日時",
    occurredAt: "発生日時",
    timeUnavailable: "記録なし",
    backfilled: "履歴から補完",
    backfilledLabel: "過去データの補完",
    backfilledValue: (backfilled) => backfilled ? "はい" : "いいえ",
    correction: "訂正",
    supersedes: "以前の記録を置き換えます",
    evidence: "根拠",
    evidenceStates: {
      available: "利用可能",
      sourceUnavailable: "ソース利用不可",
      redacted: "秘匿済み"
    },
    entryTypes: {
      eventOccurred: "イベント発生",
      opportunitySurfaced: "商談検出",
      transactionRecorded: "取引記録",
      transactionStatusChanged: "取引ステータス変更",
      transactionCorrected: "取引訂正"
    },
    subjectTypes: {
      event: "イベント",
      opportunity: "商談",
      transaction: "取引"
    },
    inspectSource: (index) => `ソース ${index} を確認`,
    inspectSourceFor: (title, index) => `${title} のソース ${index} を確認`,
    inspectTransaction: "取引を表示",
    inspectTransactionFor: (title) => `${title} の取引詳細を表示`,
    openTransactionGraph: "グラフで開く",
    openTransactionGraphFor: (title) => `グラフで開く：${title}`,
    loadOlder: "以前の記録を読み込む",
    loadingOlder: "以前の記録を読み込み中…",
    sourceDetailTitle: "正確なソース",
    transactionDetailTitle: "現在の取引",
    transactionGraphTitle: "取引グラフ",
    transactionGraphReadOnly: "現在の事実と根拠に基づく接続 · 読み取り専用",
    closeSourceDetail: "ソース詳細を閉じる",
    closeTransactionDetail: "取引詳細を閉じる",
    closeTransactionGraph: "取引グラフを閉じる",
    loadingSource: "正確なソースを読み込み中…",
    loadingTransaction: "現在の取引を読み込み中…",
    loadingTransactionGraph: "取引グラフを読み込み中…",
    transactionGraphUnavailable: "取引グラフを利用できません。",
    selectTransactionGraphNode: "ノードを選択して根拠を確認してください。",
    exactQuote: "正確な引用",
    evidenceSpan: "根拠スパン",
    sourceFrame: "ソースフレーム",
    sourceNode: "ソースノード",
    openSourceFrame: "ソースフレームを開く",
    kind: "種別",
    externalId: "外部 ID",
    status: "ステータス",
    amount: "金額",
    parties: "関係者",
    provenance: "来歴",
    acceptedAt: "承認日時",
    evidenceText: "根拠",
    authority: "権限元",
    sourceFact: "ソース事実",
    sourceFrames: "ソースフレーム",
    evidenceSpans: "根拠スパン",
    sourceReferences: "ソース参照",
    transactionKinds: {
      contract: "契約",
      order: "注文",
      invoice: "請求書",
      payment: "支払い"
    },
    transactionStatuses: {
      committed: "確定",
      settled: "決済済み",
      cancelled: "キャンセル済み"
    }
  },
  settings: {
    navigationAriaLabel: "設定ナビゲーション",
    sectionsAriaLabel: "設定セクション",
    title: "設定",
    workspaceSettings: "ワークスペース設定",
    description: "アカウント、Agent の既定値、データを 1 か所で管理します。",
    signOut: "サインアウト",
    billingSuccess: "プランが有効になりました。Deep Research が利用可能になり、Agent credits が追加されました。",
    billingCancel: "Checkout はキャンセルされました。プランは変更されていません。",
    sidebar: {
      account: "アカウント",
      accountDescription: "ワークスペース名",
      billing: "Billing",
      billingDescription: "Messaging addon プラン",
      credits: "Credits",
      creditsDescription: "Agent credit 残高",
      computers: "コンピュータ",
      computersDescription: "agent を動かすコンピュータを接続",
      privateAgent: "Private Agent",
      privateAgentDescription: "名前・接続先・runtime を決める",
      agent: "Agent",
      agentDescription: "言語・地域・チャネル",
      data: "データ",
      dataDescription: "ワークスペースデータをエクスポート",
      advanced: "高度",
      advancedDescription: "ファイルインポートとフレーム artifacts"
    }
  },
  billing: {
    ariaLabel: "Billing",
    title: "Messaging channels",
    signIn: "messaging addon を管理するにはサインインしてください。",
    active: "Messaging addon は有効です",
    pendingCancel: "期間終了時にキャンセル",
    renews: (date) => `更新日 ${date}`,
    upsell: "Telegram、WeChat、Line delivery は有料プランに含まれます。",
    viewPlans: "プランを見る"
  },
  credits: {
    ariaLabel: "Agent credits",
    title: "Agent credits",
    signIn: "Agent credits を見るにはサインインしてください。",
    upgrade: "Deep Research を利用するには有料プランへアップグレードしてください。",
    byok: "Deep Research は自分の provider サインインまたはキーを使い、自分のコンピュータで実行されます。Salesko credits は使いません。",
    available: "credits available",
    empty: "credit activity はまだありません。",
    loadError: "credit 残高を読み込めませんでした。しばらくしてから再試行してください。"
  },
  computers: {
    ariaLabel: "コンピュータ",
    title: "コンピュータ",
    description: "Private Agent はここで接続したコンピュータ上で動きます。Salesko は調整役で、実行はあなたのマシンです。",
    signIn: "コンピュータを接続するにはサインインしてください。",
    connect: "コンピュータを接続",
    stepInstallTitle: "1 · インストールしてペアリング",
    stepInstallBody: "ワンタイムコードを作成し、生成された接続コマンドを対象の Mac に貼り付けてください。インストール、ペアリング、バックグラウンド起動を一度に行います。",
    stepInstallPrereq: "macOS 専用です（Apple シリコン / Intel）。インストーラがその Mac 向けのビルドを選び、公開されたチェックサムを検証してからインストールします。",
    stepInstallGuideLink: "インストールガイドを開く",
    setupExisting: "インストール済みの場合、この setup コマンドでペアリングとバックグラウンド起動を設定できます。",
    updateTitle: "Agent を更新 · ペアリングを保持",
    updateBody: "ペアリング済みのコンピュータのターミナルで実行すると、最新版をインストールしてバックグラウンドサービスを再起動します。既存のペアリングは保持され、新しいペアリングコードは不要です。",
    startupTitle: "起動設定",
    startupBody: "ペアリング済みのコンピュータで実行すると、バックグラウンドサービスを修復または更新できます。",
    createPairingCode: "ペアリングコードを作成",
    copy: "コピー",
    copied: "コピーしました",
    pairingCodeExpiresAt: (expiresAt) => `コードは ${expiresAt} に期限切れ`,
    stepWaitingTitle: "2 · コンピュータを待機中",
    stepWaitingBody: "上のコマンドを実行してください。チェックインし次第ここに表示されます。",
    emptyTitle: "接続済みのコンピュータはありません",
    empty: "ペアリングコードを作成して、Private Agent を動かすコンピュータを接続してください。",
    presenceIsHint: "Presence は短期間の TTL hint です。readiness や実行の証明にはなりません。",
    presenceHint: (level, expiresAt) => `${expiresAt} までの ${level} hint`,
    noPresenceHint: "現在の presence hint はありません",
    online: "オンライン",
    offline: "オフライン",
    installedRelease: (version) => `インストール済みバージョン: ${version}`,
    installedReleaseUnavailable: "インストール済みバージョン: 未報告",
    latestRelease: (version) => `最新バージョン: ${version}`,
    latestReleaseLoading: "最新バージョン: 読み込み中",
    latestReleaseUnavailable: "最新バージョン: 取得できません",
    runtimes: "検出された runtime",
    noRuntimeFacts: "このコンピュータはどの runtime を持つか報告していません。",
    signedIn: "サインイン済み",
    notSignedIn: "未サインイン",
    revoked: "取り消し済み",
    revoke: "取り消す",
    keysStayOnComputer: "provider のサインインと API キーはコンピュータ側に残ります。そこで次を実行してください:",
    updateError: "コンピュータ一覧を更新できませんでした。しばらくしてから再試行してください。"
  },
  privateAgent: {
    ariaLabel: "Private Agent",
    title: "Private Agent",
    description: "1 台のコンピュータに紐づいた自分専用の agent です。runtime のサインインは自分のもので、Salesko からは見えません。",
    signIn: "Private Agent を設定するにはサインインしてください。",
    loading: "Private Agent を読み込み中",
    name: "Agent 名",
    namePlaceholder: "agent に名前を付ける",
    computer: "コンピュータ",
    computerHint: "agent は接続済みのコンピュータ 1 台だけで動きます。",
    runtime: "Runtime",
    runtimeHint: "どれを選んでも、サインインはあなたのコンピュータに残ります。",
    piVendor: "プロバイダープリセット",
    piVendorHint: "プロバイダーを選択すると、そのモデル一覧とローカル設定コマンドを用意します。以下の設定 ID はコンピュータ上の設定と一致する必要があります。",
    piVendorPlaceholder: "プロバイダーを選択",
    piConfigureHint: "Agent のコンピュータで実行します。API キーはターミナルで非表示入力し、ローカルに保存します。この設定がデバイスの既定になります。",
    subscriptionModel: "モデル",
    runtimeDefaultModel: "ランタイムの既定モデル",
    specifyModel: "モデルを指定",
    modelId: "モデルの別名または ID",
    subscriptionModelHint: "この Agent とアカウントが対応するモデルの別名または ID を入力してください。別名が指すバージョンは更新される場合があります。利用できないモデルは実行時にエラーになります。",
    piProvider: "Pi 設定 ID",
    piProviderHint: "salesko-agent keys list の正確な profile_ref を入力します。プロバイダー名ではありません。",
    piProviderPlaceholder: "openrouter",
    piModel: "Pi モデル",
    piModelHint: "選択したプロバイダーが公開している model から選びます。先にプロバイダーを選択してください。",
    piModelPlaceholder: "model を選択",
    runtimes: {
      claudeSubscription: "Claude サブスクリプション",
      codexSubscription: "Codex サブスクリプション",
      piProviderKey: "Pi + 自分の provider key"
    },
    researchPreset: "リサーチ preset",
    presetHint: "1 つの research agent の地域 preset で、別々の agent ではありません。",
    profiles: {
      default: "デフォルト",
      mainland: "中国本土 (CN)",
      taiwan: "台湾 (TW)",
      hongKong: "香港 (HK)"
    },
    save: "Private Agent を保存",
    saved: "Private Agent を保存しました。",
    projectionPending: "バインドしたコンピューターがこの Profile を適用するのを待っています。",
    projectionSynced: "バインドしたコンピューターがこの Profile を適用しました。",
    projectionFailed: "バインドしたコンピューターはこの Profile を適用できませんでした。",
    remove: "agent を削除",
    removeConfirm: "この Private Agent を削除しますか? 作り直すまでリサーチは止まります。",
    removeConfirmAction: "削除",
    removeCancel: "残す",
    deviceNotActive: "そのコンピュータはもう有効ではありません。接続済みのものを選んでください。",
    needsRebindingTitle: "再バインドが必要",
    needsRebinding: "この agent が紐づいていたコンピュータは取り消されたか存在しません。別のものを選んで保存してください。",
    noComputersTitle: "接続済みのコンピュータはありません",
    noComputers: "まずコンピュータを接続してください。Private Agent はどこかで動く必要があります。",
    goToComputers: "コンピュータへ",
    loadError: "Private Agent を読み込めませんでした。しばらくしてから再試行してください。",
    saveError: "Private Agent を保存できませんでした。しばらくしてから再試行してください。"
  },
  privateAgentJobs: {
    ariaLabel: "Private Agent のリサーチ",
    eyebrow: "Private Agent",
    title: "Private Agent",
    signIn: "サインインすると Private Agent の作業状況を確認できます。",
    loading: "Private Agent を読み込んでいます",
    loadingJobs: "リサーチを読み込んでいます",
    loadError: "リサーチジョブを読み込めませんでした。しばらくしてから再試行してください。",
    emptyTitle: "リサーチはまだありません",
    empty: "人物や商談を開いて、Private Agent にリサーチを依頼してください。",
    noAgentTitle: "Private Agent がまだありません",
    noAgent: "エージェントに名前を付け、接続済みのコンピューターに紐付けるとリサーチできます。",
    setUpAgent: "Private Agent を設定",
    openComputers: "Computers を開く",
    computerOffline: "このエージェントが動くコンピューターはオフラインです。オンラインになると待機中のリサーチが始まります。",
    computerRevoked: "紐付けていたコンピューターは失効しています。別のコンピューターを接続して紐付け直してください。",
    computer: {
      online: "コンピューターはオンライン",
      offline: "コンピューターはオフライン",
      revoked: "コンピューターは失効",
      missing: "コンピューター未設定"
    },
    status: {
      queued: "待機中",
      dispatching: "受け渡し中",
      running: "実行中",
      cancelRequested: "キャンセル中",
      succeeded: "レビュー待ち",
      failed: "失敗",
      canceled: "キャンセル済み",
      waitingDevice: "コンピューター待ち",
      erased: "消去済み"
    },
    statusDetail: {
      waitingDevice: "接続済みのコンピューターがこのリサーチを引き受けませんでした。接続してから再実行してください。",
      queuedComputerOffline: "待機中 \u2014 コンピューターはオフライン",
      queuedNoComputer: "待機中 \u2014 接続済みのコンピューターがありません"
    },
    stopReasons: {
      completed: "完了",
      stepBudget: "ステップ上限に到達",
      timeBudget: "時間上限に到達",
      tokenBudget: "トークン上限に到達",
      providerLimit: "プロバイダー制限で停止"
    },
    stopReason: (label) => `停止理由: ${label}`,
    startedAt: (when) => `開始 ${when}`,
    jobId: (id) => `ジョブ ${id}`,
    review: "変更をレビュー",
    reviewPending: "準備中\u2026",
    cancel: "キャンセル",
    canceling: "キャンセル中\u2026",
    showDetails: "詳細",
    hideDetails: "詳細を隠す",
    loadingDetails: "詳細を読み込んでいます",
    noResultDetail: "このジョブは結果を返しませんでした。",
    frameConflict: "リサーチ開始後にグラフが変わりました。現在のグラフで実行し直してください。",
    resultNotReady: "このリサーチにはまだレビューできる結果がありません。",
    materializeError: "変更セットを準備できませんでした。しばらくしてから再試行してください。",
    changeSetSourceLabel: (agentName) => `${agentName} のリサーチ`,
    question: (entityLabel) => `${entityLabel} をリサーチし、関係グラフの更新を提案してください。`,
    trigger: {
      ariaLabel: "Private Agent のリサーチ",
      eyebrow: "Private Agent",
      action: (agentName) => `${agentName} にリサーチを依頼`,
      actionFallback: "Private Agent にリサーチを依頼",
      starting: "開始しています\u2026",
      checkingAccess: "プランを確認しています\u2026",
      signedOut: "サインインすると自分のコンピューターでリサーチを実行できます。",
      entitlementRequired: "Deep Research は有料プランに含まれています。",
      profileRequired: "先に Private Agent に名前を付け、接続済みのコンピューターに紐付けてください。",
      noActiveComputer: "Private Agent が動くコンピューターを接続してください。",
      activeJob: "Private Agent はすでにリサーチジョブを実行中です。",
      activeJobConflict: "Private Agent はすでにリサーチジョブを実行中です。終わるまで待つか、先にキャンセルしてください。",
      noCommittedFrame: "先にグラフをコミットしてください。リサーチは保存済みのグラフに対して変更を提案します。",
      viewPlans: "プランを見る",
      openJobs: "エージェントを開く",
      insufficientCredits: "Agent credits が足りずリサーチを開始できません。チャージするかプランをアップグレードしてください。",
      frameVersionUnavailable: "このグラフにはリサーチ対象のバージョンがありません。再読み込みして再試行してください。",
      createError: "リサーチを開始できませんでした。しばらくしてから再試行してください。"
    }
  },
  privateAgentWorkspace: {
    recovery: {
      queueLabel: "ターンのキュー",
      replyDelivered: "返信を保存しました",
      ended: "返信なしで終了",
      withdrawn: "実行前に停止しました",
      stopPending: "停止を要求しました。実行を確認中",
      checking: "実行状態を確認中",
      needsAttention: "返信がありません。操作が必要です",
      notStarted: "このターンは開始されませんでした",
      messageHeld: "メッセージ保留中。対応が必要です",
      messageRefused: "メッセージが拒否されました",
      cleanupUnknown: "実行終了。後処理は未確認",
      retryTurn: "このターンを再試行",
      endTurn: "このターンを終了",
      stopAndSend: "停止して送信",
      retryPending: "同じリクエストを再送",
      pendingRequest: "応答は未確認です。保存された同じ識別情報と対象でリクエストを再送します。",
      queueFull: "キューが満杯です。ターンを完了してから送信してください。",
      actionConflict: "対象が変わりました。更新してから操作を選び直してください。",
      storageFailure: "未送信：ブラウザーの保存領域が利用できないか無効です。更新して確認してください。",
      queueCount: (count) => `未完了 ${count} / 8 ターン`
    },
    ariaLabel: "Private Agent ワークスペース",
    conversationsAriaLabel: "Private Agent の会話",
    facetsAriaLabel: "Private Agent の作業ビュー",
    chat: "チャット",
    research: "リサーチ",
    recentConversations: "最近の会話",
    noConversations: "会話はまだありません。",
    loadingConversations: "会話を読み込み中",
    newConversation: "新しい会話",
    creatingConversation: "作成中...",
    messageCount: (count) => `${count} 件のメッセージ`,
    identitySubtitle: "あなたのコンピュータで動きます。答えはあなたの手元に残ります。",
    deviceLine: (deviceName, state) => `${deviceName} で実行中 · ${state}`,
    computerState: {
      online: "オンライン",
      offline: "オフライン",
      revoked: "解除済み",
      missing: "未接続"
    },
    selectConversationTitle: "会話を開始または選択",
    selectConversation: "左から会話を選ぶか、新しく始めてください。",
    setupTitle: "まずアシスタントを設定してください",
    profileRequired: "アシスタントに名前を付け、使うコンピュータを選んでから会話を始めてください。",
    computerOfflineTitle: "あなたのコンピュータがオンラインではありません",
    updateTitle: "コンピュータ上のアシスタントに更新かサインインが必要です",
    signInTitle: "サインインしてアシスタントを使う",
    signIn: "サインインすると、アシスタントとの会話を表示または開始できます。",
    openComputers: "コンピュータを見る",
    loadingConversation: "会話を読み込み中",
    transcriptAriaLabel: "会話の記録",
    emptyTranscript: "何か聞いてみましょう。例：Acme との最近のやり取りをまとめて。",
    composerLabel: "アシスタントにメッセージを送る",
    composerPlaceholder: "アシスタントにメッセージを送る...",
    send: "送信",
    sending: "送信中...",
    checkExecution: "もう一度確認",
    cancelTurn: "停止",
    cancelingTurn: "停止中...",
    elapsed: (value) => `経過 ${value}`,
    progressThinking: "考えています",
    progressUsingTool: "ツールを使用中",
    progressWaitingApproval: "あなたの確認を待っています",
    boundDeviceRevokedNotice: "この会話で使っていたコンピュータはもう使えません。新しい会話を始めてください。",
    readiness: {
      profile_missing: "チャットの前に、アシスタントに名前を付け、使うコンピュータを選んでください。",
      device_missing: "アシスタントが使っていたコンピュータが一覧にありません。「コンピュータ」で接続し直すか、接続済みのものを選んでください。",
      device_revoked: "アシスタントが使っていたコンピュータは解除されました。「コンピュータ」で接続済みのものを選んでください。",
      device_offline: "アシスタントのコンピュータがオンラインではありません。オンラインにしてから再試行してください。",
      agent_upgrade_required: (installedRelease) =>
        installedRelease
          ? `コンピュータ上の Salesko Agent が古すぎます（${installedRelease}）。更新してから再試行してください。`
          : "コンピュータ上の Salesko Agent が古すぎます。更新してから再試行してください。",
      runtime_missing: "コンピュータに Claude または Codex がインストールされていません。インストールしてサインインしてから再試行してください。",
      runtime_not_authenticated: "コンピュータの Claude または Codex にサインインしていません。そのコンピュータのターミナルでサインインしてから再試行してください。",
      toolset_not_configured: "コンピュータ上の Salesko Agent の更新と再起動が必要です。",
      runtime_unsupported_for_tools: "この会話で使っているモデルのツールでは Salesko のデータを読み取れません。新しい会話を始めてください。",
      profile_identity_unavailable: "この会話のアシスタントの設定が見つかりません。同じアシスタントを設定し直してから送信してください。"
    },
    agent: "アシスタント",
    you: "あなた",
    youInitial: "あ",
    turnStatus: {
      blocked: "対応が必要",
      queued: "待機中",
      dispatching: "コンピュータに接続中",
      running: "処理中",
      cancel_requested: "停止中",
      succeeded: "完了",
      failed: "失敗",
      canceled: "停止しました"
    },
    errors: {
      activeConflict: "アシスタントは前のメッセージをまだ処理中です。返事を待ってから送ってください。",
      profileRequired: "会話を始める前に、アシスタントの設定を終わらせてください。",
      replayGap: "この会話は再読み込みが必要です。",
      reconcileFailed: "アシスタントの状態を確認できませんでした。もう一度お試しください。",
      chatPaused: "チャットは一時停止中です。しばらくしてからもう一度お試しください。",
      requestFailed: "アシスタントは現在利用できません。しばらくしてから再試行してください。"
    },
    researchEmptyTitle: "リサーチはまだありません",
    researchEmpty: "人物や商談を開いて、アシスタントに調べてもらいましょう。",
    targetUnavailable: "リサーチ対象を表示できません",
    contextAriaLabel: "この会話で読み込まれた人・会社・商談",
    touchedEntitiesTitle: "この会話で読み込まれた人・会社・商談",
    touchedEntitiesEmpty: "アシスタントが回答中に見た人・会社・商談がここに並びます。",
    touchedEntitiesError: "これらの名前を読み込めませんでした。会話自体には影響しません。",
    touchedEntityUnavailable: "利用不可",
    openEntityInGraph: (label) => `${label} をグラフで開く`,
    detailTabsAriaLabel: "Private Agent のサイドパネル表示",
    detailTab: "詳細",
    conversationTab: "この会話"
  },

  dataTools: {
    ariaLabel: "高度なデータツール",
    title: "高度なデータツール",
    import: "インポート",
    export: "エクスポート",
    csvChangeSet: "CSV 変更セット",
    jsonFrame: "JSON フレーム",
    obsidianFiles: "Obsidian ファイル",
    activeFrame: (frameRecordId) => `現在のフレーム ${frameRecordId}`,
    needsCommittedFrame: "frame-scoped ツールを有効にするには、確定済みワークスペースフレームから設定を開いてください。"
  },
  tenantAdmin: {
    workspaceAriaLabel: "ワークスペース",
    workspaceTitle: "ワークスペース",
    signInWorkspace: "ワークスペースを管理するにはサインインしてください。",
    loadingWorkspace: "ワークスペースを読み込み中",
    workspaceName: "ワークスペース名",
    saveName: "名前を保存",
    workspaceNameSaved: "ワークスペース名を保存しました。",
    agentSettingsAriaLabel: "Agent 設定",
    agentDefaults: "Generic Agent の既定値",
    signInAgentDefaults: "Generic Agent の既定値を管理するにはサインインしてください。",
    checkingAccess: "Generic Agent の既定値を解除するため有料プランを確認中です。",
    defaultsFrozen: "プランを確認できるまで Generic Agent の既定値はロックされます。",
    defaultsLocked: "有料サブスクリプションが有効になると Generic Agent の既定値を解除できます。",
    loadingDefaults: "Generic Agent の既定値を読み込み中",
    language: "言語",
    region: "地域",
    timezone: "タイムゾーン",
    saveDefaults: "Generic Agent の既定値を保存",
    defaultsSaved: "Generic Agent の既定値を保存しました。",
    dataExportAriaLabel: "データエクスポート",
    dataExport: "データエクスポート",
    dataExportDescription: "ワークスペース内のグラフ、メモ、audit trail を ZIP でダウンロードします。",
    exportZip: "ZIP をエクスポート",
    exportStarted: "ワークスペース ZIP エクスポートを開始しました。",
    requestFailed: "ワークスペースリクエストに失敗しました。",
    locales: {
      en: "English",
      "zh-CN": "简体中文",
      "zh-TW": "繁體中文"
    }
  },
  canonicalRegistry: {
    eyebrow: "正規レジストリ",
    allTitle: "正規 ID",
    count: (count) => `テナント ID ${count} 件`,
    signedInRegistry: "サインイン済みテナントレジストリ",
    signIn: "テナント session でサインインすると、確定済みフレーム全体の正規 ID を閲覧できます。",
    unavailable: "正規レジストリは現在利用できません。",
    loading: "正規 ID を読み込み中…",
    noSearchMatch: "現在の検索に一致する正規 ID はありません。",
    empty: "正規 ID はまだありません。",
    openIdentity: "ID を開く",
    openIdentityAriaLabel: (label) => `${label} を開く`,
    projectedCounts: (nodeCount, frameCount) => `投影ノード ${nodeCount} 件、フレーム ${frameCount} 件`,
    columns: {
      actions: "操作",
      confidence: "信頼度",
      frames: "フレーム",
      identity: "ID",
      lastSeen: "最終確認",
      nodes: "ノード",
      type: "種類"
    }
  },
  mobile: {
    graph: "グラフ",
    inbox: "受信箱",
    you: "あなた",
    listView: "リスト",
    graphCaption: "ノードをタップ。大きな点ほど接続が多いことを示します",
    graphNodeAriaLabel: (label, typeLabel) => `${label}、${typeLabel}`,
    graphSelfNodeAriaLabel: (label) => `${label}、あなた`,
    primaryNavAriaLabel: "メインナビゲーション",
    notificationsTitle: "通知",
    captureMeeting: "ミーティングを取り込む",
    selectedEntity: "選択中のエンティティ",
    close: "閉じる",
    clearSearch: "検索をクリア",
    relationshipReminder: "関係リマインダー",
    relationshipReminderBody: (sourceLabel, targetLabel, days) => `${sourceLabel} -> ${targetLabel} は touchpoint window を ${days} 日超過しています。`,
    opportunitySignal: "商談",
    contractEmit: "Agent がノード、関係、タイムライン、表示視点を作ります。",
    generatingGraphFrame: "グラフフレームを生成中…",
    generateGraphFrame: "グラフフレームを生成",
    noDatedEvents: "このフレームには日付付きイベントがまだありません。",
    undated: "日付なし",
    linkedEntitiesCount: (count) => `リンク済みエンティティ · ${count}`,
    thisWeek: "保存済みグラフ履歴全体",
    opportunityHero: "新しい商談を確認し、必要なものを保存して、未スケジュールのフォローアップを追加します。",
    activeSignalsAcross: (opportunityCount) => `現在 ${opportunityCount} 件の商談を表示`,
    opportunityFeed: "商談受信箱",
    signInSignalsBody: "サインイン後、非公開の商談受信箱がここに表示されます。",
    emptySignalsBody: "保留中または保存済みの商談はありません。",
    updatedAt: (date) => `更新: ${date}`,
    evidenceCount: (count) => `evidence ${count} 件`,
    entityCount: (count) => `エンティティ ${count} 件`,
    frame: "フレーム",
    notesCaptured: "取り込んだメモです。Agent がそれぞれをグラフノードに構造化しました。",
    committedFrames: "確定済みフレーム",
    current: "現在",
    frameNodeSummary: (nodeCount, date) => `ノード ${nodeCount} 件 · ${date}`,
    noCommittedFrames: "確定済みフレームはありません",
    signInToSync: "テナントグラフアクセスを同期するにはサインインしてください",
    installedOnThisDevice: "このデバイスにインストール済み",
    installSalesko: "Salesko をインストール",
    installedPwaDescription: "全画面で開き、オフラインでも動作します",
    installReadyDescription: "ホーム画面に追加すると、全画面かつオフライン対応で使えます",
    installFallbackDescription: "Share -> Add to Home Screen で app 体験を利用できます",
    install: "インストール",
    mcpAccess: "MCP アクセス",
    signInRequired: "サインインが必要です",
    proPlanRequired: "Pro プランが必要です",
    signalsAndReminders: "シグナルとリマインダー",
    tenantFeedActive: "テナントフィード有効",
    inputLanguage: "入力言語",
    privacy: "プライバシー",
    localPreview: "ローカルプレビュー",
    graphCachedOffline: "グラフはキャッシュ済みで、接続がなくても読めます。",
    markAllRead: "すべて既読にする",
    refreshingSignals: "シグナルを更新中…",
    noNotificationsTitle: "通知はありません",
    notificationsEmpty: "関係リマインダーと商談シグナルがここに表示されます。",
    browserDefault: "ブラウザー既定",
    noMatchesFor: (query) => `「${query}」に一致する結果はありません`
  },
  connectors: {
    navigationAriaLabel: "コネクターナビゲーション",
    categoriesAriaLabel: "コネクターカテゴリ",
    brandLabel: "コネクター",
    eyebrow: "Integrations",
    title: "App Connectors",
    description: "2 方向、1 つのグラフ。外出先では Agent にメッセージしてメモを取り込み、既存アカウントからは Agent に読み取らせます。",
    searchPlaceholder: "コネクターを検索…",
    noMatchesTitle: "一致なし",
    noMatchesBody: (categoryLabel) => `${categoryLabel} コネクターに一致する検索結果はありません。`,
    tierPro: "Pro",
    comingSoon: "近日公開",
    premium: "Premium",
    upgradeToUnlock: "アップグレードして解除",
    available: "利用可能",
    connect: "接続",
    connected: "接続済み",
    manage: "管理",
    disconnect: "接続を解除",
    connectError: "Gmail 接続を更新できませんでした。もう一度お試しください。",
    calendarConnectError: "カレンダーの接続に失敗しました。もう一度お試しください。",
    connectedAccount: (email) => `接続済み：${email}`,
    oneTimeConnected: "Gmail が接続されました。",
    oneTimeError: "Gmail の接続に失敗しました。もう一度お試しください。",
    syncNow: "今すぐ同期",
    syncing: "同期中…",
    syncQueued: "同期をキューに登録しました。まもなく新しいリードが表示されます。",
    calendarSyncQueued: "カレンダー同期をキューに追加しました。",
    syncCompleted: "同期が完了しました。",
    syncError: "Gmail の同期を開始できませんでした。もう一度お試しください。",
    calendarSyncError: "カレンダー同期を開始できませんでした。もう一度お試しください。",
    lastSyncedAt: (date) => `前回の同期: ${date}`,
    lastSyncFailed: (message) => (message ? `前回の同期に失敗しました: ${message}` : "前回の同期に失敗しました。"),
    reconnectRequired: "Google のアクセス権が取り消されました。再接続してリードの同期を続けてください。",
    direction: {
      inbound: "Inbound · メモ取り込み",
      outbound: "Outbound · データソース"
    },
    lockCopy: {
      signed_out: "premium connectors を解除するにはサインインしてください。",
      checking: "premium connectors を解除するため paid Sidecar access を確認中です。",
      verify_failed: "Sidecar access を確認できるまで premium connectors はロックされたままです。",
      locked: "paid Sidecar subscription が有効になると premium connectors が解除されます。"
    },
    categories: {
      messaging: {
        eyebrow: "メモ取り込み",
        label: "Messaging",
        blurb: "これらのチャネルで個人 Agent に直接メッセージできます。Agent は同じスレッドで返信します。"
      },
      email: {
        eyebrow: "Inbox intelligence",
        label: "Email",
        blurb: "Agent にメールを読ませ、スレッドを検索可能な関係メモにします。"
      },
      contacts: {
        eyebrow: "人物同期",
        label: "Contacts",
        blurb: "すでに知っている人物を自動でグラフに取り込みます。"
      },
      calendar: {
        eyebrow: "会議シグナル",
        label: "Calendar",
        blurb: "カレンダーを同期し、すべての会議と参加者をグラフの関係シグナルにします。"
      }
    },
    items: {
      telegram: {
        displayName: "Telegram",
        tagline: "Telegram で Agent にメッセージして、外出先のメモを取り込みます。同じスレッドで返信されます。"
      },
      wechat: {
        displayName: "微信 WeChat",
        tagline: "WeChat で Agent にメッセージして、メモや連絡先を記録します。入力した場所に返信されます。"
      },
      line: {
        displayName: "Line",
        tagline: "Line で Agent にメッセージして会議メモを取り込みます。返信は同じチャットに届きます。"
      },
      gmail: {
        displayName: "Gmail",
        tagline: "Agent に Gmail を読ませて整理し、メールスレッドを検索可能な関係メモにします。"
      },
      "microsoft-email": {
        displayName: "Microsoft Email",
        tagline: "Agent が Outlook メールを読み取り、会話を検索可能な関係シグナルにします。"
      },
      "google-contacts": {
        displayName: "Google Contacts",
        tagline: "Google Contacts を同期し、すでに知っている人物をグラフに自動表示します。"
      },
      "google-calendar": {
        displayName: "Google Calendar",
        tagline: "Google カレンダーを同期し、会議と参加者を event シグナルとしてグラフに取り込みます。"
      },
      "microsoft-calendar": {
        displayName: "Microsoft Calendar",
        tagline: "Outlook / Microsoft カレンダーを同期し、会議と参加者をグラフに取り込みます。"
      },
      "apple-calendar": {
        displayName: "Apple Calendar",
        tagline: "近日公開 — CalDAV で iCloud カレンダーを同期します。"
      }
    },
    replyTemplate: {
      eyebrow: "返信自動化",
      heading: "Gmail 返信テンプレート",
      subtitle: "メール返信が有効になったとき、Agent が選べる承認済みテンプレートを管理します。",
      newTemplate: "新規テンプレート",
      emptyTitle: "テンプレートはまだありません",
      enabledLabel: "Agent が使用可能",
      enabledHint: "有効なテンプレートだけが Agent の選択対象になります。",
      requiresReviewLabel: "確認を必須にする",
      requiresReviewHint: "最終送信フローが明示的に承認されるまではオンのままにします。",
      nameLabel: "名前",
      namePlaceholder: "料金問い合わせ",
      descriptionLabel: "使う場面",
      descriptionPlaceholder: "見込み客が料金や購買手順を尋ねたときに使います。",
      subjectLabel: "件名",
      subjectPlaceholder: "Re: {{subject}}",
      bodyLabel: "本文",
      bodyPlaceholder: "Hi {{senderName}},\n\nご連絡ありがとうございます。確認して早めに返信します。\n\nBest,\n{{myName}}",
      variablesHint: "使用できる変数: {{senderName}}, {{senderEmail}}, {{subject}}, {{myName}}",
      create: "テンプレートを作成",
      creating: "作成中…",
      save: "テンプレートを保存",
      saving: "保存中…",
      saved: "テンプレートを保存しました。",
      createError: "テンプレートを作成できませんでした。もう一度お試しください。",
      saveError: "テンプレートを保存できませんでした。もう一度お試しください。",
      loadError: "保存済みテンプレートを読み込めませんでした。",
      updatedAt: (date) => `${date} に更新`
    },
    candidates: {
      eyebrow: "Inbox intelligence",
      heading: "リード候補",
      subtitle: "Agent が実在の見込み客と判定したメールです。確認をお待ちしています。",
      emptyTitle: "リード候補はまだありません",
      emptyBody: "Gmail アカウントを同期すると、Agent が見込み客と判定したメールがここに表示されます。",
      confidence: (percent) => `信頼度 ${percent}%`,
      draftReply: "返信を下書き",
      draftReplyPending: "下書き中…",
      draftReplyCreated: "Gmail の下書きを作成しました。送信前に Gmail で確認してください。",
      draftReplyError: "Gmail の下書きを作成できませんでした。もう一度お試しください。",
      draftReconnectRequired: "下書き作成を許可するため Gmail を再接続してください。",
      confirm: "確認",
      confirmPending: "確認中…",
      confirmError: "この候補を確認できませんでした。もう一度お試しください。",
      dismiss: "却下",
      dismissPending: "却下中…",
      dismissError: "この候補を却下できませんでした。もう一度お試しください。",
      needsFrame: "候補を確認する前に、ワークスペースを開いてグラフフレームを保存してください。",
      frameConflictRetry: "確認中にグラフが変更されました。更新してから再度お試しください。"
    }
  },
  schedule: {
    eyebrow: "読み取り専用アジェンダ",
    title: "スケジュール",
    range: (from, to) => `${from} ～ ${to}`,
    items: (count) => `${count} 件`,
    signInToLoad: "サインインして非公開のスケジュールを表示します。",
    loading: "スケジュールを読み込み中…",
    unavailable: "スケジュールは一時的に利用できません。",
    calendarTitle: "カレンダーイベント",
    calendarEmpty: "この期間にカレンダーイベントはありません。",
    untitledEvent: "無題のカレンダーイベント",
    timeUnavailable: "時刻を取得できません",
    followUpsTitle: "未スケジュールのフォローアップ",
    followUpsEmpty: "日程調整待ちのフォローアップはありません。",
    followUpRequested: (date) => `フォローアップ依頼 ${date}`,
    remindersTitle: "再訪する関係",
    remindersEmpty: "対応が必要な古い関係はありません。",
    lastSeen: (date) => `最終接点 ${date}`,
    staleByDays: (days) => `${days} 日超過`,
    truncated: "この上限付き応答の外にさらに項目があります。",
    close: "スケジュールを閉じる"
  },
  currentLeads: {
    eyebrow: "アクティブなリード",
    title: "現在",
    activeCount: (count) => `現在 ${count} 件`,
    emptyTitle: "現在にはまだ何もありません",
    emptyBody: "メールのリード候補を確認するか、任意のノードを現在としてマークすると、ここに表示されます。",
    viewInGraph: "グラフで表示",
    removeFromCurrent: "現在から削除",
    sourceEmail: "メールから"
  },
  mcpConsent: {
    title: "MCP アクセスを許可",
    description: "この MCP クライアントが Salesko グラフにアクセスできる範囲を選択し、アクセスを許可してください。",
    scopeGraphReadLabel: "グラフの読み取り",
    scopeGraphReadHint: "人物、会社、イベント、商談を検索・閲覧します。",
    scopeCalendarReadLabel: "カレンダーの読み取り",
    scopeCalendarReadHint: "今後のカレンダーの予定を一覧・検索します。",
    scopeGraphWriteLabel: "グラフ変更の提案",
    scopeGraphWriteHint: "人によるレビュー待ちの変更提案を送信します。自動で適用されることはありません。",
    continue: "続ける",
    signInBody: "サインインして、この MCP クライアントに Salesko グラフへのアクセスを許可してください。",
    invalidLinkHeading: "無効な認可リンク",
    invalidLinkBody: "この MCP 認可リンクが見つからないか無効です。MCP クライアント側からやり直してください。",
    authNotConfiguredBody: "この Salesko デプロイではサインインが設定されていないため、MCP アクセスを許可できません。",
    issuanceFailed: "MCP アクセスを許可できませんでした。もう一度お試しください。"
  },
  meetingPreparation: {
    ariaLabel: "会議準備",
    title: "会議準備",
    prepareAction: "ブリーフを準備",
    prepareActionTitle: "このイベントの会議ブリーフを生成",
    preparePending: "準備中…",
    idleBody: "この会議の AI ブリーフを生成します：目標、参加者、関係性のシグナル、質問案。",
    loading: "会議ブリーフを準備しています…",
    successHeading: "会議ブリーフ",
    goalTitle: "目標",
    participantsTitle: "参加者と既知の背景",
    knownContextEmpty: "参加者以外の追加背景はありません。",
    relationshipSignalsTitle: "関係性のシグナル",
    relationshipSignalsEmpty: "グラフに関係性のシグナルはまだありません。",
    watchoutsTitle: "注意点",
    watchoutsEmpty: "注意点は見つかりませんでした。",
    suggestedQuestionsTitle: "質問案",
    suggestedCloseTitle: "クロージング案",
    sourcesTitle: "ソース",
    sourcesTruncated: "この上限付き応答の外にさらにソースがあります。",
    sourceCalendarEvent: "カレンダーイベント",
    sourceCalendarAttendee: "カレンダー参加者",
    sourceGraphEdge: "関係",
    sourceEvidence: "根拠",
    sourceFrameProvenance: (frameId, acceptedAt) => `フレーム ${frameId} · 採用日 ${acceptedAt}`,
    gapsTitle: "ギャップ",
    gapsEmpty: "ギャップは見つかりませんでした — ブリーフの根拠は十分です。",
    gapNoGraphPersonMatch: (participantLabel) => `${participantLabel} に一致するグラフ上の人物が見つかりませんでした。`,
    gapNoRelationshipEvidence: "グラフに利用可能な関係性の根拠がありませんでした。",
    gapNoOpportunityEvidence: "グラフに利用可能な商談の根拠がありませんでした。",
    gapContextTruncated: (collections) => `一部の背景情報が切り詰められました：${collections}。`,
    gapOptionalCalendarDetailUnavailable: (detail) => `このカレンダーイベントには${detail}がありませんでした。`,
    calendarDetailLabels: {
      summary: "概要",
      location: "場所",
      endAt: "終了時刻",
      organizer: "主催者"
    },
    truncatedCollectionLabels: {
      calendarAttendees: "カレンダー参加者",
      graphPeople: "グラフの人物",
      graphContextNodes: "グラフの背景",
      graphEdges: "グラフの関係",
      opportunities: "商談",
      evidence: "根拠"
    },
    errorEntitlement: "会議準備機能は有料プランが必要です。アップグレードしてご利用ください。",
    errorNotEligible: "このイベントは会議ブリーフの対象ではありません。",
    errorStaleEvent: "このカレンダーイベントは古くなっています。カレンダーを同期してからもう一度お試しください。",
    errorModelError: "一時的にブリーフを生成できませんでした。もう一度お試しください。",
    errorGeneric: "この会議を準備できませんでした。もう一度お試しください。"
  }
} satisfies Dictionary;
